import { createSettingMetadataFromGroupDefaults } from "../../settings-catalog/metadata";
import type {
  SettingDefinition,
  SettingMetadata,
  SettingRegistryMetadataInput,
  TeamSettingsSettingValueSource,
} from "../../settings-catalog/types";
import {
  HsaFlatTeamSettingGroupById,
  type HsaFlatTeamSettingGroupId,
} from "./groups";

type HsaSettingInput = SettingRegistryMetadataInput;

function setting(metadata: HsaSettingInput): HsaSettingInput {
  return metadata;
}

function hsaGroup<
  const Group extends HsaFlatTeamSettingGroupId,
  const T extends Record<string, HsaSettingInput>,
>(group: Group, settings: T): { [K in keyof T]: T[K] & { group: Group } } {
  return Object.fromEntries(
    Object.entries(settings).map(([key, value]) => [key, { ...value, group }]),
  ) as { [K in keyof T]: T[K] & { group: Group } };
}

const HsaRegistry = {
  ...hsaGroup("hsa.buy_buttons", {
    "hsa/buyButtons/showOnlyHsaPaymentMethod": setting({
      label: "Show only HSA payment method when HSA buy button clicked",
      description:
        "When a shopper enters checkout through a storefront Buy with HSA/FSA button, hide the other payment methods so only HSA/FSA is offered. Turn off to keep all payment methods visible; HSA/FSA is still promoted to the top either way.",
      synonyms: [
        "show only hsa payment method when hsa buy button clicked",
        "hide other payment methods hsa",
        "hsa payment exclusivity",
        "hsa only checkout",
      ],
      valueSemantics: {
        defaultValue: true,
        falseLabel: "All payment methods shown",
        trueLabel: "Only HSA/FSA shown",
      },
    }),
  }),
};

type HsaFlatTeamSettingKey = keyof typeof HsaRegistry;
const HsaFlatTeamSettingKeys = Object.keys(
  HsaRegistry,
) as HsaFlatTeamSettingKey[];

export const HsaFlatTeamSettingCatalogDefinitions = HsaFlatTeamSettingKeys.map(
  (key) => {
    const definition = HsaRegistry[key];
    return {
      id: key,
      groupId: definition.group,
      metadata: createSettingMetadataFromGroupDefaults({
        setting: definition,
        group: HsaFlatTeamSettingGroupById[definition.group],
      }),
      valueSource: { adapter: "teamSettings", key },
    };
  },
) satisfies readonly SettingDefinition<
  HsaFlatTeamSettingKey,
  HsaFlatTeamSettingGroupId,
  TeamSettingsSettingValueSource<HsaFlatTeamSettingKey>
>[];

export const HsaFlatTeamSettingMetadataByKey = Object.fromEntries(
  HsaFlatTeamSettingCatalogDefinitions.map((definition) => [
    definition.id,
    definition.metadata,
  ]),
) as Record<HsaFlatTeamSettingKey, SettingMetadata>;
